import {
  AIIconPicker,
  AITextAreaInput,
  Button,
  Checkbox,
  Dialog,
  RoleVisibilitySelect,
  slugify,
  TextInput,
  type IconoirIconName,
} from "@vellum/ui";
import { suggestIconNames, vellumApiSdk } from "@vellum/micro-app-auth";
import {
  ReportScope,
  type ReportSchema,
  type SubscriptionType,
} from "@vellum/vellum-api";
import { useFormik } from "formik";
import * as yup from "yup";

export interface ReportFormValues {
  title: string;
  slug: string;
  description: string;
  metabase_question_id: string;
  /** "" is the no-cadence option: no date filter and no subscriptions. */
  cadence: SubscriptionType | "";
  scope: ReportScope;
  icon: string | null;
  visible_to_roles: string[];
  restrict_to_user_regions: boolean;
}

const CADENCE_OPTIONS: { label: string; value: SubscriptionType | "" }[] = [
  { label: "Daily", value: "Daily" },
  { label: "Weekly", value: "Weekly" },
  { label: "No date filter", value: "" },
];

const SCOPE_OPTIONS: { label: string; value: ReportScope }[] = [
  { label: "Nothing", value: ReportScope.NONE },
  { label: "A company", value: ReportScope.COMPANY },
  { label: "A company + facility", value: ReportScope.COMPANY_FACILITY },
];

// A daily subscription is keyed to one facility, so a daily report whose
// viewers never pick one could never be emailed — the API rejects the pair.
const DAILY_SCOPE_MESSAGE =
  "A daily report is emailed one facility at a time, so viewers must pick a company + facility.";

const validationSchema = yup.object({
  title: yup.string().trim().required("Title is required"),
  slug: yup
    .string()
    .trim()
    .matches(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      "Lowercase letters, numbers, and single hyphens only",
    )
    .notOneOf(["order"], "'order' is reserved")
    .required("Slug is required"),
  description: yup.string().trim(),
  metabase_question_id: yup
    .number()
    .typeError("Enter the Metabase question id")
    .integer("Must be a whole number")
    .positive("Must be a positive number")
    .required("Metabase question id is required"),
  // The buttons below already keep these in step; this catches an existing
  // report saved before the rule, whose form can open on the bad pair.
  scope: yup
    .mixed<ReportScope>()
    .test("scope-supports-cadence", DAILY_SCOPE_MESSAGE, function (value) {
      return (
        this.parent.cadence !== "Daily" ||
        value === ReportScope.COMPANY_FACILITY
      );
    }),
});

interface ReportFormDialogProps {
  isOpen: boolean;
  /** The report being edited, or null when creating a new one. */
  report: ReportSchema | null;
  onClose: () => void;
  onSubmit: (values: ReportFormValues) => Promise<void>;
}

export function ReportFormDialog({
  isOpen,
  report,
  onClose,
  onSubmit,
}: ReportFormDialogProps) {
  const formik = useFormik<ReportFormValues>({
    enableReinitialize: true,
    initialValues: {
      title: report?.title ?? "",
      slug: report?.slug ?? "",
      description: report?.description ?? "",
      metabase_question_id: report ? String(report.metabase_question_id) : "",
      cadence: report?.cadence ?? "",
      scope: report?.scope ?? ReportScope.COMPANY,
      icon: report?.icon ?? null,
      visible_to_roles: report?.visible_to_roles ?? [],
      restrict_to_user_regions: report?.restrict_to_user_regions ?? false,
    },
    validationSchema,
    onSubmit: async (values) => {
      await onSubmit(values);
    },
  });

  const errorFor = (field: keyof ReportFormValues) =>
    formik.touched[field]
      ? (formik.errors[field] as string | undefined)
      : undefined;

  // A new report's slug follows its title until the slug is edited by hand; an
  // existing report's slug is left alone, since changing it breaks its links.
  const handleTitleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    formik.handleChange(event);
    const followsTitle =
      !report && formik.values.slug === slugify(formik.values.title);
    if (followsTitle) {
      void formik.setFieldValue("slug", slugify(event.target.value));
    }
  };

  // A protected report's slug is what Luna's embed URLs resolve through, so
  // the API refuses to change it. Show it read-only rather than let a save fail.
  const slugLocked = report?.protected ?? false;

  const dailyCadence = formik.values.cadence === "Daily";

  // Picking Daily drags the scope along with it: the two are only independent
  // as far as the weekly/no-cadence cases: see DAILY_SCOPE_MESSAGE.
  const handleCadenceChange = (value: SubscriptionType | ""): void => {
    void formik.setFieldValue("cadence", value);
    if (value === "Daily") {
      void formik.setFieldValue("scope", ReportScope.COMPANY_FACILITY);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={report ? "Edit report" : "Add report"}
    >
      <form onSubmit={formik.handleSubmit} className="mt-4 flex flex-col gap-4">
        <TextInput
          id="title"
          name="title"
          label="Title"
          value={formik.values.title}
          onChange={handleTitleChange}
          onBlur={formik.handleBlur}
          color={errorFor("title") ? "error" : undefined}
          errortext={errorFor("title")}
        />
        <TextInput
          id="slug"
          name="slug"
          label="Slug"
          disabled={slugLocked}
          helpertext={
            slugLocked
              ? `Report URL: /reports/${formik.values.slug} — fixed, because this report is linked to from outside the reporting app.`
              : `Report URL: /reports/${formik.values.slug || "…"}`
          }
          value={formik.values.slug}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          color={errorFor("slug") ? "error" : undefined}
          errortext={errorFor("slug")}
        />
        <AITextAreaInput
          id="description"
          name="description"
          label="Description"
          rows={2}
          className="resize-y"
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          onSuggest={async (currentValue) => {
            const result = await vellumApiSdk.suggestDescription({
              body: {
                subject: formik.values.title.trim() || "an operational report",
                context: "A Metabase report in the Vellum reporting app",
                current_text: currentValue.trim() || null,
              },
            });
            if (result.data === undefined) {
              throw new Error("Suggestion request failed");
            }
            return result.data.suggestion;
          }}
          onSuggestion={(suggestion) =>
            void formik.setFieldValue("description", suggestion)
          }
        />
        <TextInput
          id="metabase_question_id"
          name="metabase_question_id"
          type="number"
          label="Metabase question id"
          helpertext="The number in the Metabase question's URL."
          value={formik.values.metabase_question_id}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          color={errorFor("metabase_question_id") ? "error" : undefined}
          errortext={errorFor("metabase_question_id")}
        />
        <div>
          <span className="mb-1 block text-sm font-medium text-gray-700">
            Cadence
          </span>
          <div className="flex gap-2">
            {CADENCE_OPTIONS.map((option) => (
              <Button
                key={option.label}
                type="button"
                variant={
                  formik.values.cadence === option.value ? "primary" : "outline"
                }
                className={
                  formik.values.cadence === option.value
                    ? undefined
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }
                onClick={() => handleCadenceChange(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
          <p className="mt-1.5 text-xs text-gray-500">
            Sets the report&apos;s date window, and the schedule it is emailed
            on when a subscription is attached.
          </p>
        </div>
        <div>
          <span className="mb-1 block text-sm font-medium text-gray-700">
            Viewers first pick
          </span>
          <div className="flex flex-wrap gap-2">
            {SCOPE_OPTIONS.map((option) => {
              // Daily pins the scope, so offer the incompatible options as
              // disabled rather than let the pair be saved and break later.
              const locked =
                dailyCadence && option.value !== ReportScope.COMPANY_FACILITY;
              return (
                <Button
                  key={option.value}
                  type="button"
                  disabled={locked}
                  title={locked ? DAILY_SCOPE_MESSAGE : undefined}
                  variant={
                    formik.values.scope === option.value ? "primary" : "outline"
                  }
                  className={
                    formik.values.scope === option.value
                      ? undefined
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }
                  onClick={() =>
                    void formik.setFieldValue("scope", option.value)
                  }
                >
                  {option.label}
                </Button>
              );
            })}
          </div>
          {/* Not gated on `touched`: scope is set by button, never focused. */}
          {formik.errors.scope ? (
            <p className="mt-1.5 text-xs text-error">{formik.errors.scope}</p>
          ) : (
            <p className="mt-1.5 text-xs text-gray-500">
              {dailyCadence
                ? DAILY_SCOPE_MESSAGE
                : formik.values.scope === ReportScope.NONE
                  ? "The report opens straight to the data, and cannot be emailed on a subscription."
                  : "The report is filtered to what the viewer picks."}
            </p>
          )}
        </div>
        <AIIconPicker
          label="Icon"
          value={formik.values.icon}
          onChange={(icon: IconoirIconName | null) =>
            void formik.setFieldValue("icon", icon)
          }
          subject={formik.values.title}
          context={formik.values.description}
          onSuggest={suggestIconNames}
        />
        <RoleVisibilitySelect
          itemLabel="report"
          value={formik.values.visible_to_roles}
          onChange={(roles) =>
            void formik.setFieldValue("visible_to_roles", roles)
          }
        />
        <Checkbox
          id="restrict_to_user_regions"
          name="restrict_to_user_regions"
          label="Limit region admins to their own regions"
          helperText="Only for a Metabase question with a locked allowed_regions filter on the region id. Admins still see every region."
          checked={formik.values.restrict_to_user_regions}
          onChange={formik.handleChange}
        />
        <div className="mt-2 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={formik.isSubmitting}>
            {report ? "Save changes" : "Add report"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
